import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Plus, Sparkles, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api, { formatPaise } from '@/lib/api';
import AdPlanCard from '@/components/AdPlanCard';
import PricingExplainer from '@/components/PricingExplainer';
import TopUpDialog from '@/components/TopUpDialog';

/** Plans and pricing. Customers who have used all their ads are sent here from the ad chat and
 * "Make an ad"; once they buy a plan they can go straight back to making one. */
export default function PricingPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [wasBlocked, setWasBlocked] = useState(false);
  const [balance, setBalance] = useState(0);
  const [razorpayActive, setRazorpayActive] = useState(false);
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [planKey, setPlanKey] = useState(0); // remounts the plan card so it shows the new plan

  const load = async () => {
    try {
      const [{ data: plan }, { data: wallet }] = await Promise.all([api.get('/api/wallet/plan'), api.get('/api/wallet/balance')]);
      setStatus(plan);
      if (!plan.can_make_ad) setWasBlocked(true);
      setBalance(wallet.balance_paise);
      setRazorpayActive(wallet.razorpay_active);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const outOfAds = status && !status.can_make_ad;
  return (
    <div data-testid="pricing-page" className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight font-heading mb-1">Plans &amp; pricing</h1>
        <p className="text-sm text-muted-foreground">Your first ad is free. After that, pick a plan for more ads.</p>
      </div>

      {outOfAds && (
        <div className="p-4 rounded-xl bg-amber-500/10 border-2 border-amber-500/40 flex items-start gap-3" data-testid="out-of-ads">
          <AlertTriangle className="w-5 h-5 text-amber-700 dark:text-amber-400 shrink-0 mt-0.5" aria-hidden="true" />
          <div>
            <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">You&apos;ve used all your ads</p>
            <p className="text-xs mt-1">Pick a plan below to make more. Ads you&apos;ve already made, and ads that are running, aren&apos;t affected.</p>
          </div>
        </div>
      )}
      {wasBlocked && status?.can_make_ad && (
        <div className="p-4 rounded-xl bg-money-soft border-2 border-money/40 flex flex-wrap items-center justify-between gap-3" data-testid="ready-to-make">
          <p className="text-sm font-semibold text-money flex items-center gap-2"><CheckCircle2 className="w-5 h-5" aria-hidden="true" /> You&apos;re all set to make your next ad.</p>
          <Button className="gradient-bg !font-extrabold" onClick={() => navigate('/create-ad')}>
            <Sparkles className="w-4 h-4" /> Make an ad
          </Button>
        </div>
      )}

      <div className="neo-card p-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-xl bg-money-soft border-2 border-ink flex items-center justify-center">
            <Wallet className="w-5 h-5 text-money" aria-hidden="true" />
          </span>
          <div>
            <p className="text-xs text-muted-foreground">Wallet balance</p>
            <p className="text-xl font-extrabold font-heading text-money">{formatPaise(balance)}</p>
          </div>
        </div>
        <Button className="gradient-bg" disabled={!razorpayActive} onClick={() => setTopUpOpen(true)} data-testid="pricing-top-up">
          <Plus className="w-4 h-4" /> Add money
        </Button>
      </div>

      <AdPlanCard key={planKey} onBought={() => { setPlanKey(k => k + 1); load(); }}
        onNeedMoney={() => razorpayActive && setTopUpOpen(true)} />
      <PricingExplainer />

      <TopUpDialog open={topUpOpen} onOpenChange={setTopUpOpen} onPaid={() => load()} />
    </div>
  );
}
